import React, { useMemo, useState, useCallback, isValidElement } from 'react'
import cx from 'classnames'
import { useUniqueId } from 'hooks'

import { WidthContainer } from 'components/layout'

import useQueryTabs from './util/useQueryTabs'
import useTabsKeyboard from './util/useTabsKeyboard'

import Tab from './Tab/Tab'
import Content from './Content/Content'
import type { TabsContentProps } from './Content/Content'

import s from './Tabs.scss'


type Child = React.ReactElement<TabsContentProps>

export type TabsProps = {
  children: Array<Child>
  className?: string
  activeTab?: string
  defaultActiveTab?: string
  withWidthContainer?: boolean
  withQuery?: boolean // sync active tab with query parameter
  withScroll?: boolean // scroll to top of page on active tab change
  justify?: 'justify-start' | 'justify-around' | 'justify-center'
  onChange?: (id: string) => void
}

type TabsComponent = React.FunctionComponent<TabsProps> & {
  Content: React.CFC<TabsContentProps>
}

const Tabs: TabsComponent = (props) => {
  const { children, className, justify = 'justify-start', activeTab: controllableActiveTab, defaultActiveTab,
    withWidthContainer = false, withQuery = false, withScroll = true, onChange } = props

  const tabsId = useUniqueId('tabs')

  const availableTabIds = useMemo(() => (
    React.Children.map<TabsContentProps['id'], Child>(children, (child) => child?.props?.id).filter(Boolean)
  ), [ children ])

  const state = useState(defaultActiveTab || availableTabIds[0])
  const queryState = useQueryTabs({ availableTabIds, defaultActiveTab, withScroll })
  let activeTab
  let setActiveTab

  if (controllableActiveTab && typeof onChange === 'function') {
    activeTab = controllableActiveTab
    setActiveTab = onChange
  }
  else {
    [ activeTab, setActiveTab ] = withQuery ? queryState : state
  }

  const tabsRef = useTabsKeyboard({ availableTabIds, activeTab, setActiveTab })

  const handleClick = useCallback((id) => {
    if (activeTab === id) {
      return
    }

    setActiveTab(id)

    if (typeof onChange === 'function') {
      onChange(id)
    }
  }, [ activeTab, setActiveTab, onChange ])

  const tabsClassName = cx(s.tabs, justify)

  const tabs = (
    <WidthContainer className={s.tabsContainer} ignore={!withWidthContainer}>
      <div ref={tabsRef} className={tabsClassName} role="tablist">
        {
          React.Children.map<React.ReactElement, Child>(children, (child, index) => {
            if (!isValidElement(child)) {
              return child
            }

            const { id, title } = child.props
            const isActive = id === activeTab
            const withMargin = justify === 'justify-start' || justify === 'justify-center'

            return (
              <Tab
                key={id}
                id={id}
                tagId={`${tabsId}-${index}`}
                title={title}
                isActive={isActive}
                withMargin={withMargin}
                onClick={handleClick}
              />
            )
          })
        }
      </div>
    </WidthContainer>
  )

  const activeTabIndex = availableTabIds.indexOf(activeTab)
  const activeTabControlId = `${tabsId}-${activeTabIndex}`

  return (
    <div className={className}>
      {tabs}
      <div
        id={`${tabsId}-${activeTabIndex}-panel`}
        role="tabpanel"
        tabIndex={0}
        aria-labelledby={activeTabControlId}
      >
        {children[activeTabIndex]}
      </div>
    </div>
  )
}

Tabs.Content = Content


export default Tabs
